import { useCallback, useRef } from 'react'
import {
  DEFAULT_TEXT,
  DEFAULT_TEXT_DURATION_S,
  newId,
  type TextOverlay,
  type ToolProject,
} from '../model'
import type { VirtualTimeline } from '../timeline'

/** How far past a caption's start selecting it parks the playhead. Long
 * enough to clear the frame the browser snaps back to, far shorter than
 * the shortest caption a tag can be dragged to. */
const SELECT_NUDGE_S = 0.05

export interface TextOverlaysOptions {
  onChange: (updater: (project: ToolProject) => ToolProject) => void
  texts: TextOverlay[]
  /** Read through a ref: "add a caption here" needs the current position
   * and the current axis length, not a dependency on either. */
  timeline: VirtualTimeline
  playhead: number
  seekTo: (time: number) => void
  pause: () => void
  onSelect: (id: string | null) => void
}

export interface TextOverlaysApi {
  addText: () => void
  patchText: (id: string, patch: Partial<TextOverlay>) => void
  deleteText: (id: string) => void
  duplicateText: (id: string) => void
  moveText: (id: string, x: number, y: number) => void
  editTextContent: (id: string, text: string) => void
  /** Select **and** park the playhead where the caption is visible. */
  selectTextAndSeek: (id: string | null) => void
}

/** Everything that writes to ``project.texts``, plus the selection gestures
 * that only make sense alongside those writes. */
export function useTextOverlays({
  onChange,
  texts,
  timeline,
  playhead,
  seekTo,
  pause,
  onSelect,
}: TextOverlaysOptions): TextOverlaysApi {
  const playheadRef = useRef(playhead)
  playheadRef.current = playhead
  const timelineRef = useRef(timeline)
  timelineRef.current = timeline
  const textsRef = useRef(texts)
  textsRef.current = texts

  const addText = useCallback(() => {
    const id = newId('txt')
    const start = playheadRef.current
    onChange((p) => ({
      ...p,
      texts: [
        ...p.texts,
        {
          ...DEFAULT_TEXT,
          id,
          start,
          // Clamped so a caption added near the end still has a span the
          // timeline can draw and the user can grab.
          end: start + DEFAULT_TEXT_DURATION_S,
        },
      ],
    }))
    onSelect(id)
  }, [onChange, onSelect])

  const patchText = useCallback(
    (id: string, patch: Partial<TextOverlay>) => {
      onChange((p) => ({
        ...p,
        texts: p.texts.map((t) => (t.id === id ? { ...t, ...patch } : t)),
      }))
    },
    [onChange],
  )

  const deleteText = useCallback(
    (id: string) => {
      onChange((p) => ({ ...p, texts: p.texts.filter((t) => t.id !== id) }))
      onSelect(null)
    },
    [onChange, onSelect],
  )

  /**
   * Copy a caption whole — text, font, colour, position, size, background,
   * transitions — and drop it **immediately after** the original.
   *
   * Landing it on top of the source would stack the two tags and hide the
   * copy behind the thing it came from; placing it after gives it its own
   * lane space and makes it obvious something happened. It only falls back
   * to the source's own span when there isn't room left on the timeline.
   *
   * The playhead follows it, because a copy you can't see is a copy you
   * can't reposition — which is the whole point of duplicating.
   */
  const duplicateText = useCallback(
    (id: string) => {
      const source = textsRef.current.find((t) => t.id === id)
      if (!source) return
      const total = timelineRef.current.duration
      const length = source.end - source.start
      const start = source.end + length <= total ? source.end : Math.max(0, total - length)
      const copy: TextOverlay = {
        ...source,
        id: newId('txt'),
        start,
        end: Math.min(total, start + length),
      }
      onChange((p) => ({ ...p, texts: [...p.texts, copy] }))
      onSelect(copy.id)
      seekTo(copy.start)
    },
    [onChange, onSelect, seekTo],
  )

  const moveText = useCallback(
    (id: string, x: number, y: number) => {
      patchText(id, { x, y })
    },
    [patchText],
  )

  const editTextContent = useCallback(
    (id: string, text: string) => {
      patchText(id, { text })
    },
    [patchText],
  )

  /**
   * Selecting a caption parks the playhead at the moment it appears.
   *
   * Otherwise clicking a tag selects something that isn't on screen: the
   * inspector fills in, the caption is nowhere to be seen, and dragging it
   * into position is impossible. Pausing first stops playback from
   * carrying the cursor straight back out of the caption's span.
   */
  const selectTextAndSeek = useCallback(
    (id: string | null) => {
      onSelect(id)
      if (!id) return
      const target = textsRef.current.find((t) => t.id === id)
      if (!target) return
      pause()
      // A frame *inside* the caption, not its first instant. A seek lands
      // on the nearest decodable frame, which at a shared boundary is the
      // last frame of the previous caption — so clicking the second tag
      // showed the first caption.
      seekTo(target.start + SELECT_NUDGE_S)
    },
    [onSelect, pause, seekTo],
  )

  return {
    addText,
    patchText,
    deleteText,
    duplicateText,
    moveText,
    editTextContent,
    selectTextAndSeek,
  }
}
